import { motion } from 'framer-motion';
import {
  Target,
  TrendingUp,
  Users,
  BarChart3,
  Zap,
  CheckCircle,
  ArrowRight,
  Layers,
  Map,
  Compass,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function StrategicFrameworkPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const steps = [
    {
      number: '01',
      icon: Compass,
      title: 'Foundation',
      subtitle: 'Build Your Strategic Core',
      description:
        'Define your vision, mission, values, and strategic objectives. This is where clarity begins.',
      features: [
        "Vision Statement - Where you're headed",
        'Mission Statement - Why you exist',
        'Core Values - What you stand for',
        "Strategic Objectives - What you'll achieve",
      ],
      link: '/foundation',
    },
    {
      number: '02',
      icon: Map,
      title: 'External Assessment',
      subtitle: 'Understand Your Market',
      description: 'Analyze your competitive landscape, industry forces, and market opportunities.',
      features: [
        "Porter's Five Forces Analysis",
        'PESTEL Analysis (Political, Economic, Social, etc.)',
        'Competitive Positioning',
        'Market Trends & Opportunities',
      ],
      link: '/external-assessment',
    },
    {
      number: '03',
      icon: Layers,
      title: 'Internal Assessment',
      subtitle: 'Know Your Capabilities',
      description: 'Evaluate your strengths, weaknesses, resources, and internal capabilities.',
      features: [
        'SWOT Analysis (Strengths, Weaknesses, Opportunities, Threats)',
        'Core Competencies',
        'Resource Audit',
        'Organizational Capabilities',
      ],
      link: '/internal-assessment',
    },
    {
      number: '04',
      icon: Target,
      title: 'Strategy Formulation',
      subtitle: 'Define Your Path Forward',
      description:
        'Choose strategic initiatives that will move you from where you are to where you want to be.',
      features: [
        'Strategic Options Analysis',
        'Competitive Advantage Selection',
        'Growth Strategies',
        'Strategic Initiatives Prioritization',
      ],
      link: '/strategy-formulation',
    },
    {
      number: '05',
      icon: TrendingUp,
      title: 'Execution & Monitoring',
      subtitle: 'Turn Strategy Into Results',
      description:
        'With Pro Activation, connect strategic priorities to projects, tasks and clear ownership.',
      features: [
        'Strategic Projects & Milestones',
        'Task Assignment & Tracking',
        'Project & Task Progress',
        'Progress Monitoring & Reviews',
      ],
      link: '/execution-monitoring',
    },
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'A clear starting point',
      description:
        'Use familiar strategic frameworks in a clear sequence, so each decision builds on the last.',
    },
    {
      icon: Zap,
      title: 'Help when you need it',
      description:
        'With Pro Activation, draft ideas with AI, compare options and refine the direction using your company context.',
    },
    {
      icon: Users,
      title: 'A plan your team can own',
      description:
        'Bring your people into the plan and connect strategic priorities to clear responsibilities.',
    },
    {
      icon: BarChart3,
      title: 'Progress you can review',
      description:
        'Review project and task progress in the context of the strategic priorities they support.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Strategic Framework' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        title="Turn a clear direction into a plan your team can deliver."
        subtitle="Connect your vision, market insight and priorities to the people and projects that move them forward. One guided journey, from the first decision to the next action."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Build my strategic plan
        </Link>
      </PageHero>

      {/* Framework Overview */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Five connected steps. One shared direction.</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start with your company direction, understand the market and your capabilities, then
              choose priorities and connect them to delivery. Each step gives the next one context.
            </p>
          </motion.div>

          {/* Steps */}
          <div className="space-y-16 max-w-6xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-12 top-24 w-0.5 h-32 bg-gradient-to-b from-blue-200 to-purple-200 hidden md:block" />
                )}

                <div className="public-card bg-white rounded-2xl border border-gray-200 p-8 hover:border-blue-300 transition-colors">
                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Number and Icon */}
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <div className="text-7xl font-bold text-gray-100">{step.number}</div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-br from-blue-100 to-purple-100 w-20 h-20 rounded-xl flex items-center justify-center">
                          <step.icon className="w-10 h-10 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-3xl font-bold mb-2">{step.title}</h3>
                      <p className="text-blue-600 font-semibold mb-4">{step.subtitle}</p>
                      <p className="text-gray-600 mb-6 text-lg">{step.description}</p>

                      <div className="grid md:grid-cols-2 gap-3 mb-6">
                        {step.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Link
                        to={step.link}
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                      >
                        Learn More About This Step <ArrowRight className="w-5 h-5 ml-2" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Keep the reasoning connected to the work.</h2>
            <p className="text-xl text-gray-600">
              Keep your direction, assessments and delivery plan in one connected workspace.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-6 rounded-xl shadow-sm"
              >
                <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It's Different */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Your strategy belongs in the work, not a slide deck.
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="public-card bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4 text-red-600">
                  Traditional Strategic Planning
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li>❌ Annual planning retreats only</li>
                  <li>❌ Expensive consultants required</li>
                  <li>❌ Plans sit in PowerPoint decks</li>
                  <li>❌ No connection to day-to-day work</li>
                  <li>❌ Hard to track progress</li>
                  <li>❌ Unclear next steps after the plan is written</li>
                </ul>
              </div>

              <div className="public-card bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold mb-4 text-blue-600">StratPlan Framework</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>✅ Continuous planning process</li>
                  <li>✅ AI guidance available with Pro Activation</li>
                  <li>✅ Living strategic plan</li>
                  <li>✅ Connected to execution & tasks</li>
                  <li>✅ Projects and tasks connected to priorities</li>
                  <li>✅ A clear next step at each stage</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="public-section public-cta py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Give your ambition a clear plan.
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Work through five clear steps and build a plan that connects your company direction to
              the priorities, people and projects moving it forward.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Build my strategic plan <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
